import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Minus, Plus, ShoppingCart, X } from 'lucide-react';
import { priceFor, inr, QUALITY_LABELS, QUALITY_DESC, imgUrl } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

export default function ProductDetailModal({ product, open, onOpenChange }) {
    const { addItem } = useCart();
    const [quality, setQuality] = useState('prime');
    const [qty, setQty] = useState(1);

    useEffect(() => { if (open) setQty(1); }, [open, product]);

    if (!product) return null;
    const price = priceFor(product, quality);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl p-0 rounded-none border border-slate-200 overflow-hidden" data-testid="product-modal">
                <button onClick={() => onOpenChange(false)} className="absolute top-3 right-3 z-10 bg-white/90 w-9 h-9 flex items-center justify-center hover:bg-white" aria-label="Close">
                    <X className="w-4 h-4" />
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                    <div className="product-img-bg aspect-square md:aspect-auto flex items-center justify-center p-6">
                        <img src={imgUrl(product.image_url)} alt={product.name}
                            onError={(e) => { e.currentTarget.src = 'data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 200 200%22><rect width=%22200%22 height=%22200%22 fill=%22%23F1F5F9%22/><text x=%2250%25%22 y=%2250%25%22 text-anchor=%22middle%22 dy=%22.35em%22 font-family=%22Outfit%22 font-size=%2214%22 fill=%22%23475569%22>' + encodeURIComponent(product.sku) + '</text></svg>'; }}
                            className="w-full h-full object-contain" />
                    </div>
                    <div className="p-6 md:p-8 flex flex-col">
                        <div className="eyebrow mb-2">{product.category}</div>
                        <h2 className="font-display text-2xl font-bold text-slate-900 leading-tight">{product.name}</h2>
                        <div className="text-xs text-slate-500 mt-2">SKU: <span className="font-mono">{product.sku}</span> · {product.size}</div>
                        <p className="text-sm text-slate-600 mt-4 leading-relaxed">{product.description}</p>

                        <div className="mt-6 space-y-4">
                            <div>
                                <div className="eyebrow mb-2">Quality Tier</div>
                                <Select value={quality} onValueChange={setQuality}>
                                    <SelectTrigger className="rounded-none h-11" data-testid="modal-quality-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="eco">Eco · {QUALITY_DESC.eco}</SelectItem>
                                        <SelectItem value="prime">Prime · {QUALITY_DESC.prime}</SelectItem>
                                        <SelectItem value="gold">Gold · {QUALITY_DESC.gold}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex items-center gap-4">
                                <div>
                                    <div className="eyebrow mb-2">Quantity</div>
                                    <div className="flex items-center border border-slate-300">
                                        <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 hover:bg-slate-100 flex items-center justify-center" data-testid="modal-qty-minus">
                                            <Minus className="w-4 h-4" />
                                        </button>
                                        <span className="w-10 text-center font-semibold" data-testid="modal-qty">{qty}</span>
                                        <button onClick={() => setQty(qty + 1)} className="w-10 h-10 hover:bg-slate-100 flex items-center justify-center" data-testid="modal-qty-plus">
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="eyebrow mb-2">{QUALITY_LABELS[quality]} Price</div>
                                    <div className="font-display text-2xl font-bold text-blue-900">{inr(price * qty)}</div>
                                </div>
                            </div>
                        </div>

                        <Button
                            data-testid="modal-add-to-cart"
                            onClick={() => { addItem(product, qty, quality); toast.success(`${product.name} added to cart`); onOpenChange(false); }}
                            className="mt-6 bg-amber-600 hover:bg-amber-700 rounded-none py-5"
                        >
                            <ShoppingCart className="w-4 h-4 mr-2" /> Add to Cart
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
